import React, { Suspense } from "react";
import { IntlProvider } from "react-intl";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { media, style } from "typestyle";
import BackendRequest from "./components/BackendRequest";
import Footer from "./components/Footer";
import Header, { HeaderSkeleton } from "./components/Header";
import { OrganizationEventListSkeleton } from "./screens/OrganizationEventList";
import { EventViewSkeleton } from "./screens/Event";
import ScrollToTop from "./components/ScrollToTop";
import { FullscreenSpinner, SpinnerContainer } from "./components/Spinner";
import { fetchOrganization } from "./dao/organization";
import * as storage from "./helpers/storage";
import mediaQueries, { upFrom, upTo } from "./styles/mediaQueries";
import { ReservationContextProvider } from "./hooks/useReservationExpiry";
import MediaQuery from "react-responsive";
import ReservationExpiring from "./components/ReservationExpiring";
import { PriceModeContextProvider } from "./hooks/usePriceMode";
import messages_en from "./translations/en-GB.json";
import messages_no from "./translations/nb-NO.json";
import { NO, GB } from "country-flag-icons/react/3x2";
import { FormattedMessage } from "react-intl";
import { LocaleConfig, Locale } from "./types";
import { CurrentLangContextProvider } from "./hooks/useCurrentLang";

const contentClx = style({
  flex: `1 0 auto`,
});

const headerClx = style(
  {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    textAlign: "center",
    overflow: "hidden",
    display: "block",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    width: "calc(100vw - 160px)",
  },
  media(upFrom(mediaQueries.tablet), {
    width: "calc(100vw - 310px)",
  }),
  media(upFrom(mediaQueries.desktopSmall), {
    width: "calc(100vw - 480px)",
  }),
);

type Segment = string | number;

export const paths = {
  organization: () => "/",
  event: (eventId: Segment) => `/event/${eventId}`,
  forms: (eventId: Segment) => `/event/${eventId}/information`,
  addons: (eventId: Segment) => `/event/${eventId}/addons`,
  checkout: (eventId: Segment, checkoutMethod?: Segment) =>
    checkoutMethod ? `/event/${eventId}/checkout/${checkoutMethod}` : `/event/${eventId}/checkout`,
  checkoutV2: (
    eventId: Segment,
    checkoutId: Segment,
    checkoutHash: Segment,
    checkoutMethod?: Segment,
  ) =>
    checkoutMethod
      ? `/event/${eventId}/checkout/${checkoutMethod}/checkout_id/${checkoutId}/h/${checkoutHash}`
      : `/event/${eventId}/checkout`,
  orderIntentApproved: (eventId: Segment, checkoutId: Segment) =>
    `/event/${eventId}/checkout/invoice/order_intent/${checkoutId}`,
  complete: () => `/complete`,
  querySafeComplete: (checkoutId: Segment, checkoutHash: Segment) =>
    `/complete/checkout_id/${checkoutId}/h/${checkoutHash}`,
  querySafeBusinessComplete: (completionParams: Segment) =>
    `/complete/completion_params/${completionParams}`,
};

const LazyOrganizationEventList = React.lazy(() => import("./screens/OrganizationEventList"));
const LazyCheckout = React.lazy(() => import("./screens/Checkout"));
const LazyForms = React.lazy(() => import("./screens/Forms"));
const LazyComplete = React.lazy(() => import("./screens/Complete"));
const LazyBusinessInvoice = React.lazy(
  () => import("./screens/Checkout/Checkouts/BusinessInvoice"),
);
const LazyBusinessInvoiceOrderIntent = React.lazy(
  () => import("./screens/Checkout/Checkouts/BusinessInvoiceOrderIntent"),
);
const LazyFree = React.lazy(() => import("./screens/Checkout/Checkouts/FreeCheckout"));
const LazyAddons = React.lazy(() => import("./screens/AddonProducts"));
const LazyEvent = React.lazy(() => import("./screens/Event"));

const isOrg =
  window.location.pathname === paths.organization() ||
  window.location.pathname === paths.organization() + "/";

const isEvent = /^(\/event\/\d+\/?)$/g.test(window.location.pathname);

const loadingFallback = isOrg ? (
  <OrganizationEventListSkeleton />
) : isEvent ? (
  <EventViewSkeleton />
) : (
  <SpinnerContainer height={500} />
);

const Loadable: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return <Suspense fallback={loadingFallback}>{children}</Suspense>;
};

export const availableLocales: LocaleConfig = {
  no: {
    name: "nb-NO",
    alternate: "nb_NO",
    short: "NO",
    flag: <NO title="Norwegian" />,
    text: <FormattedMessage id={"components.Header.norwegian"} defaultMessage={"Norwegian"} />,
    messages: messages_no,
  },
  en: {
    name: "en-GB",
    alternate: "en_GB",
    short: "EN",
    flag: <GB title="English" />,
    text: <FormattedMessage id={"components.Header.english"} defaultMessage={"English"} />,
    messages: messages_en,
  },
};

const App: React.FC = () => {
  const [customerType, setCustomerType] = React.useState(
    storage.getItem("customer_type", "PRIVATE"),
  );
  const filteredAvailableLocales: LocaleConfig = Object.entries(availableLocales)
    .filter(([k, v]) => window.AVAILABLE_LOCALES.includes(v.alternate))
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {} as LocaleConfig);

  const getLangQuery = (): Locale => {
    // Check query params for lang-query
    const sp = window.location.search.split("&");
    sp.forEach((q) => {
      const m = q.match("lang");
      if (m?.input?.split("=")[1].includes("en")) {
        storage.setItem("sales-language", "en");
      } else if (m?.input?.split("=")[1].includes("nb")) {
        storage.setItem("sales-language", "no");
      }
    });
    // Get key from storage to see if the provided languages support the selected locale
    const maybe = storage.getItem(
      "sales-language",
      Object.keys(filteredAvailableLocales)[0],
    ) as Locale;
    if (Object.keys(filteredAvailableLocales).includes(maybe)) {
      // Our set includes the language, return it
      return maybe;
    } else {
      // No match, override and write first element to storage and default to this
      storage.setItem("sales-language", Object.keys(filteredAvailableLocales)[0] as Locale);
      return Object.keys(filteredAvailableLocales)[0] as Locale;
    }
  };

  let i18nConfig = {
    locale: getLangQuery(),
    messages: filteredAvailableLocales[getLangQuery()].messages,
  };

  function onChangeLanguage(lang: Locale) {
    storage.setItem("sales-language", lang);
    i18nConfig.messages = filteredAvailableLocales[lang].messages;
    i18nConfig.locale = lang;
  }

  const [lang, setLang] = React.useState<Locale>(
    storage.getItem("sales-language", Object.keys(filteredAvailableLocales)[0]) as Locale,
  );

  const onChangeLang = (lang: Locale) => {
    setLang(lang);
    onChangeLanguage(lang);
  };

  return (
    <React.StrictMode>
      <CurrentLangContextProvider
        lang={lang}
        onChangeLanguage={onChangeLang}
        i18nConfig={i18nConfig}
        filteredAvailableLocales={filteredAvailableLocales}
      >
        <IntlProvider
          messages={i18nConfig.messages}
          key={i18nConfig.locale}
          locale={i18nConfig.locale}
          defaultLocale={Object.values(filteredAvailableLocales)[0].name}
        >
          <ReservationContextProvider>
            <BackendRequest requestFn={fetchOrganization}>
              {{
                loading: () => (
                  <BrowserRouter>
                    <ScrollToTop />
                    <div className={contentClx}>
                      <HeaderSkeleton />
                      <Loadable>
                        <Routes>
                          <Route path={"/"} element={<OrganizationEventListSkeleton />} />

                          <Route path={`/event/:eventId/checkout`} element={<Outlet />}>
                            <Route
                              path={`/event/:eventId/checkout/free`}
                              element={<FullscreenSpinner />}
                            />
                            <Route
                              path={`/event/:eventId/checkout/invoice/checkout_id/:checkoutId/h/:checkoutHash`}
                              element={<FullscreenSpinner />}
                            />
                            <Route
                              path={`/event/:eventId/checkout/invoice/order_intent/:checkoutId`}
                              element={<FullscreenSpinner />}
                            />
                          </Route>

                          <Route
                            path={`/event/:eventId/information`}
                            element={<FullscreenSpinner />}
                          />

                          <Route path={`/event/:eventId/addons`} element={<FullscreenSpinner />} />

                          <Route path={`/event/:eventId`} element={<EventViewSkeleton />} />

                          <Route path={`/event`} element={<Navigate to={"/"} />} />

                          <Route path={`/complete`} element={<FullscreenSpinner />} />
                        </Routes>
                      </Loadable>
                    </div>
                    <Footer />
                  </BrowserRouter>
                ),
                error: (error) => <div>Error fetching org, ({JSON.stringify(error)})</div>,
                success: (organization) => {
                  return (
                    <PriceModeContextProvider
                      priceMode={organization.priceMode}
                      setCustomerType={setCustomerType}
                      customerType={customerType}
                    >
                      <BrowserRouter>
                        <ScrollToTop />
                        <div className={contentClx}>
                          <Header
                            title={<span className={headerClx}>{organization.name}</span>}
                            organization={organization}
                          />
                          <Loadable>
                            <Routes>
                              <Route
                                path={"/"}
                                element={<LazyOrganizationEventList organization={organization} />}
                              />
                              <Route
                                element={
                                  <>
                                    <MediaQuery {...upTo(mediaQueries.mobile)}>
                                      <ReservationExpiring isMobile />
                                    </MediaQuery>
                                    <Outlet />
                                  </>
                                }
                              >
                                <Route
                                  path={`/event/:eventId/checkout`}
                                  element={<LazyCheckout organization={organization} />}
                                >
                                  <Route
                                    path={`/event/:eventId/checkout/free`}
                                    element={<LazyFree />}
                                  />
                                  <Route
                                    path={`/event/:eventId/checkout/invoice/checkout_id/:checkoutId/h/:checkoutHash`}
                                    element={<LazyBusinessInvoice />}
                                  />
                                  <Route
                                    path={`/event/:eventId/checkout/invoice/order_intent/:checkoutId`}
                                    element={<LazyBusinessInvoiceOrderIntent />}
                                  />
                                </Route>

                                <Route
                                  path={`/event/:eventId/information`}
                                  element={<LazyForms />}
                                />

                                <Route
                                  path={`/event/:eventId/addons`}
                                  element={<LazyAddons organization={organization} />}
                                />

                                <Route
                                  path={`/event/:eventId`}
                                  element={<LazyEvent organization={organization} />}
                                />

                                <Route path={`/event`} element={<Navigate to={"/"} />} />
                              </Route>
                              {/* Need separate routes of higher priority than complete to handle the query-safe version of complete */}
                              <Route
                                path={`/complete/completion_params/:completionParams`}
                                element={<LazyComplete />}
                              />

                              <Route
                                path={`/complete/checkout_id/:checkoutId/h/:checkoutHash`}
                                element={<LazyComplete />}
                              />
                              <Route path={`/complete`} element={<LazyComplete />} />
                            </Routes>
                          </Loadable>
                        </div>
                        <Footer />
                      </BrowserRouter>
                    </PriceModeContextProvider>
                  );
                },
              }}
            </BackendRequest>
          </ReservationContextProvider>
        </IntlProvider>
      </CurrentLangContextProvider>
    </React.StrictMode>
  );
};

export default App;
